/**
 * 管理员仪表板 - 管理员功能总入口
 */

import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import UserManagement from './UserManagement';
import InvitationManagement from './InvitationManagement';
import SystemStats from './SystemStats';

type AdminTab = 'overview' | 'users' | 'invitations' | 'settings';

interface AdminDashboardProps {}

const AdminDashboard: React.FC<AdminDashboardProps> = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<AdminTab>('overview');
  const [stats] = useState({
    totalUsers: 0,
    verifiedUsers: 0,
    totalInvitations: 0,
    newUsersToday: 0,
  });

  // 检查管理员权限
  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="text-red-500 text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">权限不足</h2>
          <p className="text-gray-600 mb-4">您没有访问管理员后台的权限</p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            返回首页
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: '系统概览', icon: '📊' },
    { id: 'users', name: '用户管理', icon: '👥' },
    { id: 'invitations', name: '邀请码管理', icon: '🎫' },
    { id: 'settings', name: '系统设置', icon: '⚙️' },
  ] as const;

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <SystemStats />;
      case 'users':
        return <UserManagement />;
      case 'invitations':
        return <InvitationManagement />;
      case 'settings':
        return (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">系统设置</h3>
            <p className="text-gray-600">系统设置功能开发中...</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航栏 */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">管理员后台</h1>
              <span className="ml-3 px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                超级管理员
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                欢迎，{user.email}
              </span>
              <button
                onClick={() => window.location.href = '/'}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                返回前台
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex">
          {/* 左侧导航 */}
          <div className="w-64 bg-white rounded-lg shadow p-6 mr-8">
            <nav className="space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-500'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <span className="text-xl mr-3">{tab.icon}</span>
                  <span className="font-medium">{tab.name}</span>
                </button>
              ))}
            </nav>

            {/* 快速统计 */}
            <div className="mt-8 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 mb-3">快速统计</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">总用户数</span>
                  <span className="font-medium">{stats.totalUsers}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">已验证用户</span>
                  <span className="font-medium">{stats.verifiedUsers}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">今日新增</span>
                  <span className="font-medium text-green-600">{stats.newUsersToday}</span>
                </div>
              </div>
            </div>
          </div>

          {/* 主要内容区域 */}
          <div className="flex-1">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 